import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { APP_NAME } from "@/lib/brand";
import { CircuitBoard, Send, ShieldCheck } from "lucide-react";

const TASK_KINDS = ["general", "medical", "lifestyle"];

export default function AssistantPage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [taskKind, setTaskKind] = useState("general");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  async function send(e) {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text) return;
    setError("");
    setBusy(true);
    const optimistic = { role: "user", content: text };
    setMessages((m) => [...m, optimistic]);
    setInput("");
    try {
      const { data } = await api.post("/brain/chat", {
        message: text,
        session_id: sessionId,
        task_kind: taskKind,
        language,
      });
      setSessionId(data.session_id);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: data.reply,
          provider: data.provider,
          model: data.model,
          safety_note: data.safety_note,
        },
      ]);
    } catch (e) {
      const raw = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      const isUnavailable = e.response?.status === 503;
      setError(
        isUnavailable
          ? language === "tr"
            ? "Mi Brain AI servisi şu anda kullanılamıyor. Lütfen kısa süre sonra tekrar deneyin."
            : "Mi Brain AI service is temporarily unavailable. Please try again shortly."
          : raw
      );
    } finally {
      setBusy(false);
    }
  }

  function newSession() {
    setSessionId(null);
    setMessages([]);
    setError("");
  }

  return (
    <div className="flex h-full flex-col gap-6" data-testid="assistant-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            {t.layer.brain} · {t.layer.brainSub}
          </div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">
            {t.brain.title}
          </h1>
          <p className="mt-1 text-sm text-slate-500">{t.brain.sub}</p>
        </div>
        <button type="button" className="mios-btn mios-btn-ghost" onClick={newSession} data-testid="new-session-button">
          {t.brain.newSession}
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-2" data-testid="task-kind-row">
        {TASK_KINDS.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setTaskKind(k)}
            data-testid={`task-kind-${k}`}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              taskKind === k
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white text-slate-600 hover:text-slate-900"
            }`}
          >
            {k === "medical" ? t.brain.taskMedical : k === "lifestyle" ? t.brain.taskLifestyle : t.brain.taskGeneral}
          </button>
        ))}
      </div>

      <div className="mios-card flex flex-1 flex-col" data-testid="chat-shell">
        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5" style={{ minHeight: 320 }} data-testid="chat-scroll">
          {messages.length === 0 && (
            <EmptyChat t={t} user={user} />
          )}
          {messages.map((m, i) => (
            <ChatBubble key={i} message={m} />
          ))}
          {busy && (
            <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="chat-thinking">
              <CircuitBoard className="h-4 w-4 animate-pulse" />
              {t.brain.thinking}
            </div>
          )}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="chat-error">
              {error}
            </div>
          )}
        </div>
        <form onSubmit={send} className="flex items-end gap-2 border-t border-slate-200 px-4 py-3" data-testid="chat-form">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(e);
              }
            }}
            rows={1}
            placeholder={t.brain.placeholder}
            className="mios-input min-h-[40px] resize-none"
            data-testid="chat-input"
          />
          <button
            type="submit"
            disabled={busy || !input.trim()}
            className="mios-btn mios-btn-primary"
            data-testid="chat-send-button"
          >
            <Send className="h-4 w-4" /> {t.brain.send}
          </button>
        </form>
        <div className="flex items-center gap-2 border-t border-slate-100 bg-slate-50 px-4 py-2 text-[11px] text-slate-500">
          <ShieldCheck className="h-3.5 w-3.5" />
          {t.brain.safetyNote}
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ message }) {
  const isUser = message.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${message.role}`}>
      <div
        className={`max-w-[80%] rounded-xl px-4 py-3 text-sm leading-relaxed ${
          isUser
            ? "bg-slate-900 text-white"
            : "border border-slate-200 bg-white text-slate-800"
        }`}
      >
        <div className="whitespace-pre-wrap">{message.content}</div>
        {!isUser && message.provider && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
            <span className="mios-pill mios-pill-normal mono">{message.provider}</span>
            <span className="mono">{message.model}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyChat({ t, user }) {
  const suggestions =
    user.role === "clinician"
      ? [
          "Bu hastanın son lab değerlerini özetle.",
          "Hangi vital trendlerine dikkat etmeliyim?",
        ]
      : user.role === "administrator"
      ? [
          `${APP_NAME} Brain'in yönlendirme tablosunu açıkla.`,
          "Sağlayıcı arızalarında fallback nasıl çalışır?",
        ]
      : [
          "Uyku düzenimi nasıl iyileştirebilirim?",
          "Son lab değerlerimde dikkat etmem gereken bir şey var mı?",
        ];

  return (
    <div className="mx-auto max-w-md py-8 text-center" data-testid="chat-empty">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-slate-900 text-white">
        <CircuitBoard className="h-5 w-5" />
      </div>
      <div className="text-sm font-medium text-slate-900">{t.brain.title}</div>
      <div className="mt-1 text-xs text-slate-500">{t.brain.sub}</div>
      <div className="mt-4 grid gap-2 text-left">
        {suggestions.map((s, i) => (
          <div key={i} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700">
            {s}
          </div>
        ))}
      </div>
    </div>
  );
}
